import axios from 'axios';
import emojiRegex from 'emoji-regex';
import { t } from "../i18nTranslate";
import { useNavigate } from "react-router-dom";
import { PACOCA_URL } from './vars';
import{ highlightThemeWords } from "../hooks/useThemeEffects";;

// ─── Palavras que ativam o efeito ────────────────────────────────────────────
export const COPA_KEYWORDS = [
    "brasil",
    "brasileira",
    "brasileiro",
    "brazil",
    "cbf",
];

export const LGBT_KEYWORDS = [
    "lgbt",
    "lgbtqia+",
    "gay"
];

export function errorMessage(err: any) {
    if (err.status == 401){
        return t("generics.errors.sessionExpired");
    }

    else if (err.status == 429 && err.message.blocked)
        return t("generics.errors.tooManyRequests", { minutes: err.remaining });

    else if (err.status == 429){
        if(err.response.data.message == "blocked"){
           return t("generics.errors.tooManyRequests", { minutes: err.remaining });
        }
        
        if (err?.response?.data?.message)
            return err.response.data.message;

        return t("generics.errors.tooManyRequestsGeneric");
    }

    else if (err?.response?.data?.message) {
        return err.response.data.message;

    } else if (err?.response?.data.error) {
        return t("generics.errors.serverError", { error: err.response.data.error });

    } else if (err.response?.data.erro) { //gerencianet
        return err.response.data.erro;

    } else if (err.message) { //gerencianet
        return err.message;

    } else if (err.request) {
        if(err.code == "ERR_NETWORK")
            return  t("generics.errors.networkError");

        return t("generics.errors.genericServerError");
    } else {
        return t("generics.errors.genericError");
    }
}

export const speak = (text: string, lang = "pt-BR") => {
    if (!("speechSynthesis" in window)) {
        alert("Seu navegador não suporta áudio.");
        return;
    }

    const utterance = new SpeechSynthesisUtterance(text);
    utterance.lang = lang;
    utterance.rate = 1; // velocidade
    utterance.pitch = 1; // tom

    window.speechSynthesis.cancel(); // para áudio anterior
    window.speechSynthesis.speak(utterance);
};


export function censorText(text: string) {
    const savedMode = localStorage.getItem('hide-censorship');
    if(savedMode !== 'true'){
        blacklist.forEach((word) => {
            const pattern = new RegExp(`\\b${word}\\b`, 'gi');
            const replacement = `${word.charAt(0)}${'*'.repeat(word.length - 1)}`;
            text = text.replace(pattern, replacement);
        });
    }
    return text;
}

export function isValidGifUrl(url?: string) {
    try {
        if(!url?.trim().length) return false;
        const parsed = new URL(url);
        
        return parsed.hostname.startsWith("media") && parsed.hostname.endsWith("giphy.com");
    } catch (e){
        return false;
    }
}
    

// deixa com no máximo 3 linhas
export function normalizeLineBreaks(text: string, maxBreaks = 3) {
  if (!text) return '';

  // 1. Converte \r\n → \n
  text = text.replace(/\r\n/g, '\n');

  // 2. Remove múltiplos \n seguidos → apenas 1
  text = text.replace(/\n{2,}/g, '\n');

  // 3. Limita o TOTAL de quebras
  let breaks = 0;
  text = text.replace(/\n/g, (match) => {
    breaks++;
    return breaks <= maxBreaks ? '\n' : ' ';
  });

  return text;
}


export function hasOffensiveWords(text: string) {
    return blacklist.some((word) => {
        const pattern = new RegExp(`\\b${word}\\b`, 'i');
        return pattern.test(text);
    });
}

export function isWebView(): boolean {
const userAgent = navigator.userAgent || navigator.vendor;
const isStandalone = (navigator as any).standalone; // Evita erro de TypeScript

return (
    /FBAN|FBAV|Instagram|WebView|wv/i.test(userAgent) ||
    (isStandalone && navigator.platform === "iPhone")
);
}

  
// ─── Palavras destacadas por tema ─────────────────────────────────────────────
export { highlightThemeWords } from "../hooks/useThemeEffects";
  
export function clearText(text: string, scapeAll=true) {
    if(text){
        let hasCode = false;

        // --- 1. Extrai blocos de código markdown ---
        const codeBlocks: string[] = [];
        text = text.replace(/```(\w+)?\n([\s\S]*?)\n```/g, (match, lang, code) => {
            const allowedLangs = ['html', 'js','javascript', 'php', 'jsx', 'java','python','css'];
            const language = allowedLangs.includes(lang?.toLowerCase()) ? lang.toLowerCase() : null;

            hasCode = true

            const escapedCode = escapeHtml(code); // apenas o código
            const block = language
                ? `<pre><code class="language-${language}">${escapedCode}</code></pre>`
                : escapedCode;

            codeBlocks.push(block);
            return `[[CODE_BLOCK_${codeBlocks.length-1}]]`;
        });

        // Agora escapa **somente o texto restante**
        text = text.replace(/\[\[CODE_BLOCK_(\d+)]]/g, '[[[PLACEHOLDER_$1]]]'); // protege placeholders
        text = escapeHtml(text, scapeAll);
        text = text.replace(/\[\[\[PLACEHOLDER_(\d+)]]]/g, '[[CODE_BLOCK_$1]]'); // restaura

        // Reinsere os blocos de código
        codeBlocks.forEach((block, i) => {
            text = text.replace(`[[CODE_BLOCK_${i}]]`, block); // aqui não escapa de novo
        });

        // --- 4. Inline code: `const x = 1`
        text = text.replace(/`([^`]+)`/g, '<code class="language-javascript">$1</code>');

        // --- Formatações estilo WhatsApp ---
        text = text.replace(/\*(.*?)\*/g, '<strong>$1</strong>'); // *negrito*
        text = text.replace(/(^|\s)_(.*?)_(?=\s|$)/g, '$1<em>$2</em>'); // _italico_
        text = text.replace(/~(.*?)~/g, '<del>$1</del>'); // ~tachado~
        // --- Listas com * ---
        text = text.replace(/(?:^|\n)\* (.+)/g, '\n<li>$1</li>');

        // agrupa os <li> consecutivos dentro de <ul>
        text = text.replace(/(<li>[\s\S]*?<\/li>)/g, (match) => {
            const items = match
                .split('\n')
                .filter(Boolean)
                .join('');
            return `<ul>${items}</ul>`;
        });
        
        const padraoLink = /(https?:\/\/[^\s]+?)([.,!?;:()[\]{}]+)?(?=\s|$)/g;
        const padraoTagUser = /(?:^|\s)(@(?!\.)(?=[^\s]*[\p{L}0-9_])(?:[\p{L}0-9_\p{So}\p{Emoji_Presentation}]|(?<=\p{L}|\d)_*|\.(?=[\p{L}\d_]))+)/gu;
        const padraoHashtag = /(?:^|[\s>])(#[\wÀ-ÿ]+)(?![\wÀ-ÿ])/gu;


        // if(!hasCode)
        //     text = escapeHtml(text);
        
        text = text.replace(padraoLink, (match, url, punct) => {
            const isPacoca = url.startsWith("https://pacoca.net");
            const target = isPacoca ? "" : "target='_blank'";
            return `<a href='${url}' ${target}>${url}</a>${punct ?? ""}`;
        });
        // text = text.replace(padraoLink, (url) => `<a href='${url}' target='_blank' style='word-wrap: break-word;'>${url}</a>`);
        
        text = text.replace(padraoTagUser, (match) => {
            const username = match.trim().slice(1);
            return `<a class='link-username' href='/@${username}' style='word-wrap: break-word;'>${match}</a>`;
        });
        
        // Substitui múltiplos \n por um único \n
        if(!hasCode){
            text = text.replace(/\n{3,}/g, '\n');
            text = text.replace(/\n/g, '<br>');
        }

        text = text.replace(padraoHashtag, (match, tag) => {
            return match.replace(
                tag,
                `<a class='link-hashtag' href='/post/hashtag/${tag.slice(1)}' style='word-wrap: break-word;'>${tag}</a>`
            );
        });

        
        let replacedText = replaceEmojis(text);

       const trimmed = replacedText.trim();

        // Mensagem inteira contendo apenas emojis
        const onlyEmoji = trimmed.match(
            /^(?:<img[^>]*class=['"]emoji['"][^>]*>\s*)+$/
        );

        // Linha contendo apenas emojis/stickers
        const emojiOnlyLine = trimmed.match(
            /(^|<br\s*\/?>)\s*(?:<img[^>]*class=['"]emoji['"][^>]*>\s*)+(?=<br\s*\/?>|$)/i
        );

        // Conta quantos emojis existem
        const emojiCount = (
            replacedText.match(/<img[^>]*class=['"]emoji['"][^>]*>/g) || []
        ).length;

        if (onlyEmoji || emojiOnlyLine) {

            // Mais de 1 emoji = tamanho menor
            const size = emojiCount > 3 ? 70 : (
                replacedText.includes('figurinhas') ? 120 : 60
            );

            replacedText = replacedText.replaceAll(
                /style=['"][^'"]*['"]/g,
                `style='height: ${size}px; width: ${size}px; margin-top: 7px;'`
            );
        }

        return highlightThemeWords(replacedText);
    }
    return text;

}

export const isValidCNPJ = (cnpj: string) => {
    cnpj = cnpj.replace(/\D/g, '');

    if (cnpj.length !== 14) return false;

    // Elimina sequências inválidas
    if (/^(\d)\1+$/.test(cnpj)) return false;

    const calculateDigit = (base: string, factors: number[]) => {
        let total = 0;
        for (let i = 0; i < factors.length; i++) {
            total += parseInt(base[i]) * factors[i];
        }
        const remainder = total % 11;
        return remainder < 2 ? 0 : 11 - remainder;
    };

    const base = cnpj.slice(0, 12);
    const digit1 = calculateDigit(base, [5,4,3,2,9,8,7,6,5,4,3,2]);
    const digit2 = calculateDigit(base + digit1, [6,5,4,3,2,9,8,7,6,5,4,3,2]);

    return cnpj === base + digit1 + digit2;
};

export const isValidWebsite = (url: string) => {
    try {
        const parsed = new URL(url.startsWith("http") ? url : `https://${url}`);
        return !!parsed.hostname.includes(".");
    } catch {
        return false;
    }
};

export const isValidEmail = (email: string) => {
    const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return regex.test(email);
};

export const stickers: Record<string, string> = {
    ':pacoca_bolo:': "<img src='/img/figurinhas/bolo.png' class='emoji' alt=':pacoca_bolo:' data-title=':pacoca_bolo:' style='height: 32px;'>",
    ':fpacoca_celular:': "<img src='/img/figurinhas/celular.png' class='emoji' alt=':fpacoca_celular:' data-title=':fpacoca_celular:' style='height: 32px;'>",
    ':fpacoca_confuso:': "<img src='/img/figurinhas/confuso.png' class='emoji' alt=':fpacoca_confuso:' data-title=':fpacoca_confuso:' style='height: 32px;'>",
    ':fpacoca_coracaozinho:': "<img src='/img/figurinhas/coracaozinho.png' class='emoji' alt=':fpacoca_coracaozinho:' data-title=':fpacoca_coracaozinho:' style='height: 32px;'>",
    ':fpacoca_diversidade:': "<img src='/img/figurinhas/diversidade.png' class='emoji' alt=':fpacoca_diversidade:' data-title=':fpacoca_diversidade:' style='height: 32px;'>",
    ':pacoca_smile:': "<img src='/img/figurinhas/feliz.png' class='emoji' alt=':pacoca_smile:' data-title=':pacoca_smile:' style='height: 32px;'>",
    ':fpacoca_feliz:': "<img src='/img/figurinhas/feliz.png' class='emoji' alt=':fpacoca_feliz:' data-title=':fpacoca_feliz:' style='height: 32px;'>",
    ':fpacoca_abduzido:': "<img src='/img/figurinhas/abduzido.png' class='emoji' alt=':fpacoca_abduzido:' data-title=':fpacoca_abduzido:' style='height: 32px;'>",
    ':fpacoca_baloes:': "<img src='/img/figurinhas/baloes.png' class='emoji' alt=':fpacoca_baloes:' data-title=':fpacoca_baloes:' style='height: 32px;'>",
    ':fpacoca_ceo:': "<img src='/img/figurinhas/ceo.png' class='emoji' alt=':fpacoca_ceo:' data-title=':fpacoca_ceo:' style='height: 32px;'>",
    ':fpacoca_chefe:': "<img src='/img/figurinhas/chefe.png' class='emoji' alt=':fpacoca_chefe:' data-title=':fpacoca_chefe:' style='height: 32px;'>",
    ':fpacoca_dormindo:': "<img src='/img/figurinhas/dormindo.png' class='emoji' alt=':fpacoca_dormindo:' data-title=':fpacoca_dormindo:' style='height: 32px;'>",
    ':fpacoca_moderador:': "<img src='/img/figurinhas/moderador.png' class='emoji' alt=':fpacoca_moderador:' data-title=':fpacoca_moderador:' style='height: 32px;'>",
    ':fpacoca_ola:': "<img src='/img/figurinhas/ola.png' class='emoji' alt=':fpacoca_ola:' data-title=':fpacoca_ola:' style='height: 32px;'>",
    ':fpacoca_parabens:': "<img src='/img/figurinhas/parabens.png' class='emoji' alt=':fpacoca_parabens:' data-title=':fpacoca_parabens:' style='height: 32px;'>",
    ':fpacoca_show_de_bola:': "<img src='/img/figurinhas/show_de_bola.png' class='emoji' alt=':fpacoca_show_de_bola:' data-title=':fpacoca_show_de_bola:' style='height: 32px;'>",
    ':fpacoca_chorando:': "<img src='/img/figurinhas/chorando.png' class='emoji' alt=':fpacoca_chorando:' data-title=':fpacoca_chorando:' style='height: 32px;'>",
    ':fpacoca_caixa:': "<img src='/img/figurinhas/caixa.png' class='emoji' alt=':fpacoca_caixa:' data-title=':fpacoca_caixa:' style='height: 32px;'>",
    ':fpacoca_pacoquei:': "<img src='/img/figurinhas/pacoquei.png' class='emoji' alt=':fpacoca_pacoquei:' data-title=':fpacoca_pacoquei:' style='height: 32px;'>",
    ':fpacoca_site:': "<img src='/img/figurinhas/site.png' class='emoji' alt=':fpacoca_site:' data-title=':fpacoca_site:' style='height: 32px;'>",
    ':fpacoca_skate:': "<img src='/img/figurinhas/skate.png' class='emoji' alt=':fpacoca_skate:' data-title=':fpacoca_skate:' style='height: 32px;'>",
    ':fpacoca_skate2:': "<img src='/img/figurinhas/skate2.png' class='emoji' alt=':fpacoca_skate2:' data-title=':fpacoca_skate2:' style='height: 32px;'>",
    ':fpacoca_skate3:': "<img src='/img/figurinhas/skate3.png' class='emoji' alt=':fpacoca_skate3:' data-title=':fpacoca_skate3:' style='height: 32px;'>",
    ':fpacoca_skate4:': "<img src='/img/figurinhas/skate4.png' class='emoji' alt=':fpacoca_skate4:' data-title=':fpacoca_skate4:' style='height: 32px;'>",
    ':fpacoca_basquete1:': "<img src='/img/figurinhas/basquete1.png' class='emoji' alt=':fpacoca_basquete1:' data-title=':fpacoca_basquete1:' style='height: 32px;'>",
    ':fpacoca_basquete:': "<img src='/img/figurinhas/basquete.png' class='emoji' alt=':fpacoca_basquete:' data-title=':fpacoca_basquete:' style='height: 32px;'>",
    ':fpacoca_carro:': "<img src='/img/figurinhas/carro.png' class='emoji' alt=':fpacoca_carro:' data-title=':fpacoca_carro:' style='height: 32px;'>",
    ':fpacoca_chocolate:': "<img src='/img/figurinhas/chocolate.png' class='emoji' alt=':fpacoca_chocolate:' data-title=':fpacoca_chocolate:' style='height: 32px;'>",
    ':fpacoca_chocolate2:': "<img src='/img/figurinhas/chocolate2.png' class='emoji' alt=':fpacoca_chocolate2:' data-title=':fpacoca_chocolate2:' style='height: 32px;'>",
    ':fpacoca_pirulito:': "<img src='/img/figurinhas/pirulito.png' class='emoji' alt=':fpacoca_pirulito:' data-title=':fpacoca_pirulito:' style='height: 32px;'>",
    ':fpacoca_risada:': "<img src='/img/figurinhas/risada.png' class='emoji' alt=':fpacoca_risada:' data-title=':fpacoca_risada:' style='height: 32px;'>",
    ':fpacoca_refri:': "<img src='/img/figurinhas/refri.png' class='emoji' alt=':fpacoca_refri:' data-title=':fpacoca_refri:' style='height: 32px;'>",
    ':fpacoca_batata:': "<img src='/img/figurinhas/batata.png' class='emoji' alt=':fpacoca_batata:' data-title=':fpacoca_batata:' style='height: 32px;'>",
    ':fpacoca_super:': "<img src='/img/figurinhas/super.png' class='emoji' alt=':fpacoca_super:' data-title=':fpacoca_super:' style='height: 32px;'>",
    ':fpacoca_raquete:': "<img src='/img/figurinhas/raquete.png' class='emoji' alt=':fpacoca_raquete:' data-title=':fpacoca_raquete:' style='height: 32px;'>",
    ':fpacoca_piscina:': "<img src='/img/figurinhas/piscina.png' class='emoji' alt=':fpacoca_piscina:' data-title=':fpacoca_piscina:' style='height: 32px;'>",
    ':fpacoca_banho:': "<img src='/img/figurinhas/banho.png' class='emoji' alt=':fpacoca_banho:' data-title=':fpacoca_banho:' style='height: 32px;'>",
    ':fpacoca_sorvete:': "<img src='/img/figurinhas/sorvete.png' class='emoji' alt=':fpacoca_sorvete:' data-title=':fpacoca_sorvete:' style='height: 32px;'>",
    ':fpacoca_coracao:': "<img src='/img/figurinhas/coracao.png' class='emoji' alt=':fpacoca_coracao:' data-title=':fpacoca_coracao:' style='height: 32px;'>",
    ':fpacoca_capa:': "<img src='/img/figurinhas/capa.png' class='emoji' alt=':fpacoca_capa:' data-title=':fpacoca_capa:' style='height: 32px;'>",
    ':fpacoca_coracao2:': "<img src='/img/figurinhas/coracao2.png' class='emoji' alt=':fpacoca_coracao2:' data-title=':fpacoca_coracao2:' style='height: 32px;'>",
    ':fpacoca_bicicleta:': "<img src='/img/figurinhas/bicicleta.png' class='emoji' alt=':fpacoca_bicicleta:' data-title=':fpacoca_bicicleta:' style='height: 32px;'>",
    ':fpacoca_piscina2:': "<img src='/img/figurinhas/piscina2.png' class='emoji' alt=':fpacoca_piscina2:' data-title=':fpacoca_piscina2:' style='height: 32px;'>",
    ':fpacoca_sol:': "<img src='/img/figurinhas/sol.png' class='emoji' alt=':fpacoca_sol:' data-title=':fpacoca_sol:' style='height: 32px;'>",
    ':fpacoca_melancia:': "<img src='/img/figurinhas/melancia.png' class='emoji' alt=':fpacoca_melancia:' data-title=':fpacoca_melancia:' style='height: 32px;'>",
    ':fpacoca_mel:': "<img src='/img/figurinhas/mel.png' class='emoji' alt=':fpacoca_mel:' data-title=':fpacoca_mel:' style='height: 32px;'>",
    ':fpacoca_piscina3:': "<img src='/img/figurinhas/piscina3.png' class='emoji' alt=':fpacoca_piscina3:' data-title=':fpacoca_piscina3:' style='height: 32px;'>",
    ':fpacoca_pc:': "<img src='/img/figurinhas/pc.png' class='emoji' alt=':fpacoca_pc:' data-title=':fpacoca_pc:' style='height: 32px;'>",
    ':fpacoca_cafe:': "<img src='/img/figurinhas/cafe.png' class='emoji' alt=':fpacoca_cafe:' data-title=':fpacoca_cafe:' style='height: 32px;'>",
    ':fpacoca_voando:': "<img src='/img/figurinhas/voando.png' class='emoji' alt=':fpacoca_voando:' data-title=':fpacoca_voando:' style='height: 32px;'>",
};

export const emojis: Record<string, string> = {
    ':pacoca_coracao:': "<img src='/img/emoji/zack/coracao.png' class='emoji' alt=':pacoca_coracao:' data-title=':pacoca_coracao:' style='height: 27px; width: 27px;'>",
    ':pacoca_lingua:': "<img src='/img/emoji/zack/lingua.png' class='emoji' alt=':pacoca_lingua:' data-title=':pacoca_lingua:' style='height: 27px; width: 27px;'>",
    ':pacoca_mao:': "<img src='/img/emoji/zack/mao.png' class='emoji' alt=':pacoca_mao:' data-title=':pacoca_mao:' style='height: 27px; width: 27px;'>",
    ':pacoca_oculos:': "<img src='/img/emoji/zack/oculos1.png' class='emoji' alt=':pacoca_oculos:' data-title=':pacoca_oculos:' style='height: 27px; width: 27px;'>",
    ':pacoca_vomito_arco_iris:': "<img src='/img/emoji/zack/vomito_arco_iris.png' class='emoji' alt=':pacoca_vomito_arco_iris:' data-title=':pacoca_vomito_arco_iris:' style='height: 27px; width: 27px;'>",
    ':pacoca_sorriso:': "<img src='/img/emoji/zack/sorriso.png' class='emoji' alt=':pacoca_sorriso:' data-title=':pacoca_sorriso:' style='height: 27px; width: 27px;'>",
    ':pacoca_palavrao:': "<img src='/img/emoji/zack/palavrao.png' class='emoji' alt=':pacoca_palavrao:' data-title=':pacoca_palavrao:' style='height: 27px; width: 27px;'>",
    ':pacoca_duvida:': "<img src='/img/emoji/zack/duvida.png' class='emoji' alt=':pacoca_duvida:' data-title=':pacoca_duvida:' style='height: 29px; width: 29px;'>",
    ':pacoca_choro1:': "<img src='/img/emoji/zack/choro1.png' class='emoji' alt=':pacoca_choro1:' data-title=':pacoca_choro1:' style='height: 25px; width: 26px'>",
    ':pacoca_canto:': "<img src='/img/emoji/zack/canto.png' class='emoji' alt=':pacoca_canto:' data-title=':pacoca_canto:' style='height: 25px; width: 26px'>",
    ':pacoca_raiva1:': "<img src='/img/emoji/zack/raiva.png' class='emoji' alt=':pacoca_raiva1:' data-title=':pacoca_raiva1:' style='height: 25px; width: 26px'>",
    ':pacoca_beijo_coracao:': "<img src='/img/emoji/zack/beijo_coracao.png' class='emoji' alt=':pacoca_beijo_coracao:' data-title=':pacoca_beijo_coracao:' style='height: 25px; width: 26px'>",
    ':pacoca_uau:': "<img src='/img/emoji/zack/uau.png' class='emoji' alt=':pacoca_uau:' data-title=':pacoca_uau:' style='height: 25px; width: 26px'>",
    ':pacoca_tremendo:': "<img src='/img/emoji/zack/tremendo.png' class='emoji' alt=':pacoca_tremendo:' data-title=':pacoca_tremendo:' style='height: 25px; width: 26px'>",
    ':pacoca_dormindo:': "<img src='/img/emoji/zack/dormindo.png' class='emoji' alt=':pacoca_dormindo:' data-title=':pacoca_dormindo:' style='height: 25px; width: 26px'>",
    ':pacoca_baba:': "<img src='/img/emoji/zack/baba.png' class='emoji' alt=':pacoca_baba:' data-title=':pacoca_baba:' style='height: 25px; width: 26px'>",
    ':pacoca_lagrima:': "<img src='/img/emoji/zack/lagrima.png' class='emoji' alt=':pacoca_lagrima:' data-title=':pacoca_lagrima:' style='height: 25px; width: 26px'>",
    ':pacoca_diabinho:': "<img src='/img/emoji/zack/diabinho.png' class='emoji' alt=':pacoca_diabinho:' data-title=':pacoca_diabinho:' style='height: 26px; width: 26´x'>",
    ':pacoca_anjo1:': "<img src='/img/emoji/zack/anjo.png' class='emoji' alt=':pacoca_anjo1:' data-title=':pacoca_anjo1:' style='height: 26px; width: 26´x'>",
    ':pacoca_choro_riso:': "<img src='/img/emoji/zack/choro_riso.png' class='emoji' alt=':pacoca_choro_riso:' data-title=':pacoca_choro_riso:' style='height: 26px; width: 26´x'>",
    ':pacoca_palhaco:': "<img src='/img/emoji/zack/palhaco.png' class='emoji' alt=':pacoca_palhaco:' data-title=':pacoca_palhaco:' style='height: 30px; width: 30px'>",
    ':pacoca_assustado:': "<img src='/img/emoji/zack/assustado.png' class='emoji' alt=':pacoca_assustado:' data-title=':pacoca_assustado:' style='height: 30px;'>",
    ':pacoca_caveira:': "<img src='/img/emoji/zack/caveira.png' class='emoji' alt=':pacoca_caveira:' data-title=':pacoca_caveira:' style='height: 30px; width: 30px'>",
    ':pacoca_derretendo:': "<img src='/img/emoji/zack/derretendo.png' class='emoji' alt=':pacoca_derretendo:' data-title=':pacoca_derretendo:' style='height: 30px;'>",
    ':pacoca_careta:': "<img src='/img/emoji/zack/careta.png' class='emoji' alt=':pacoca_careta:' data-title=':pacoca_careta:' style='height: 30px;'>",
    ':pacoca_fome:': "<img src='/img/emoji/zack/fome.png' class='emoji' alt=':pacoca_fome:' data-title=':pacoca_fome:' style='height: 30px; width: 30px'>",
    ':pacoca_choro:': "<img src='/img/emoji/zack/choro.png' class='emoji' alt=':pacoca_choro:' data-title=':pacoca_choro:' style='height: 30px; width: 30px'>",
    ':pacoca_anjo:': "<img src='/img/emoji/zack/asinha.png' class='emoji' alt=':pacoca_anjo:' data-title=':pacoca_anjo:' style='height: 33px; width: 33px'>",
    ':pacoca_festa:': "<img src='/img/emoji/zack/festa.png' class='emoji' alt=':pacoca_festa:' data-title=':pacoca_festa:' style='height: 30px; width: 30px'>",
    ':pacoca_fundo:': "<img src='/img/pacoca-sem-braco-rounded.png' class='emoji' alt=':pacoca_fundo:' data-title=':pacoca_fundo:' style='height: 25px; width: 25px'>",
    ':pacoca_fundo_braco:': "<img src='/img/pacoca-com-braco-rounded.png' class='emoji' alt=':pacoca_fundo_braco:' data-title=':pacoca_fundo_braco:' style='height: 25px; width: 25px'>",
    ':pacoca_cat:': "<img src='/img/nyan-cat.gif' class='emoji' alt=':pacoca_cat:' data-title=':pacoca_cat:' style='margin-right: -14px; height: 20px; width: 40px';>",
    ':pacoca_404:': "<img src='/img/errors/page-not-found.jpg' class='emoji' alt=':pacoca_404:' data-title=':pacoca_404:' style='height: 30px; width: 30px'>",
    ':pacoca_500:': "<img src='/img/errors/pato.png' class='emoji' alt=':pacoca_500:' data-title=':pacoca_500:' style='height: 25px; width: 25px'>",
    ':bochecha:': "<img src='/img/emoji/bochecha.png' class='emoji' alt=':bochecha:' data-title=':bochecha:' style='height: 25px; width: 25px'>",
    ':pacoca_raiva:': "<img src='/img/emoji/raiva.png' class='emoji' alt=':pacoca_raiva:' data-title=':pacoca_raiva:' style='height: 30px; width: 30px'>",
    ':pacoca_cowboy:': "<img src='/img/emoji/paçocacowboy.png' class='emoji' alt=':pacoca_cowboy:' data-title=':pacoca_cowboy:' style='height: 30px; width: 30px'>",
};

export const emojiMap: Record<string, string> = {
    "🥰": ":pacoca_coracao:",
    "🙌": ":pacoca_mao:",
    "👐": ":pacoca_mao:",
    "😎": ":pacoca_oculos1:",
    "🤮": ":pacoca_vomito_arco_iris:",
    "😁": ":pacoca_sorriso:",
    "🙂": ":pacoca_sorriso:",
    "😃": ":pacoca_sorriso:",
    "🤬": ":pacoca_palavrao:",
    "🤔": ":pacoca_duvida:",
    "💭": ":pacoca_duvida:",
    "😭": ":pacoca_choro1:",
    "😢": ":pacoca_lagrima:",
    "😡": ":pacoca_raiva1:",
    "😘": ":pacoca_beijo_coracao:",
    "😯": ":pacoca_uau:",
    "😵‍💫": ":pacoca_tremendo:",
    "😴": ":pacoca_dormindo:",
    "🤤": ":pacoca_baba:",
    "👿": ":pacoca_diabinho:",
    "😇": ":pacoca_anjo1:",
    "😂": ":pacoca_choro_riso:",
    "🤣": ":pacoca_choro_riso:",
    "😹": ":pacoca_choro_riso:",
    "🫠": ":pacoca_derretendo:",
    "😱": ":pacoca_assustado:",
    "😨": ":pacoca_assustado:",
    "🫢": ":pacoca_assustado:",
    "😦": ":pacoca_assustado:",
    "😝": ":pacoca_careta:",
    "💀": ":pacoca_caveira:",
    "🤠": ":pacoca_cowboy:",
    ":pacoca_beijo:": ":pacoca_beijo_coracao:",
    ":pacoca_oculos1:": ":pacoca_oculos:",
    ":pacoca_diabinho1:": ":pacoca_diabinho:",
};

export function recordToEmojiMart(record: Record<string, string>) {
  return Object.entries(record).map(([key, html]) => {
    const id = key.replace(/:/g, '')

    const srcMatch = html.match(/src=['"]([^'"]+)['"]/)
    const src = srcMatch ? srcMatch[1] : ''

    const keywords = id.replace(/^f?pacoca_/, '').split('_')

    return {
      id,
      name: id,
      keywords,
      skins: [{ src }],
    }
  })
}


const emojisAndStickers = {
    ...stickers,
    ...emojis,
};

export function extractYouTubeId(text: string): string | null {
    const regex =
        /(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/;

    const match = text?.match(regex);
    return match ? match[1] : null;
}

export function removeYouTubeLinks(text: string): string {
    return text.replace(
        /(?:https?:\/\/)?(?:www\.)?(?:youtube\.com\/(?:watch\?v=|shorts\/)|youtu\.be\/)[^\s<]+/gi,
        ''
    ).trim();
}

export async function fetchLinkPreview(url: string) {
    try {
        if(!url.length) return null;
        
        const res = await axios.get(
            `https://shiny-cake-0a17.pacoca150723.workers.dev/?url=${encodeURIComponent(url)}`
        );

        return res.data;
    } catch (error) {
        return null
    }
}

export function extractFirstUrl(text: string): string | null {
    const regex = /(https?:\/\/[^\s]+)/g;
    const match = text?.match(regex);
    return match ? match[0] : null;
}


export function replaceEmojis(text: string) {
    return replaceDefaultEmojis(Object.keys(emojisAndStickers).reduce((acc, key) => {
        return acc.replace(new RegExp(key, 'g'), emojisAndStickers[key]);
    }, text));
}

export function replaceDefaultEmojis(text: string) {
    const regex = emojiRegex();

    return text.replace(regex, (emoji) => {
        const nome = emojiMap[emoji];
        if (nome && emojis[nome]) {
            return emojis[nome];
        }
        return emoji;
    });
}

// Função auxiliar para escapar HTML
function escapeHtml(text: string, scapeAll = true) {
    if(scapeAll)
        return text.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;").replace(/'/g, "&#039;");

    const allowedTags = ['p', 'br', 'strong', 'em', 'span', 'div', 'b', 'i', 'u', 'blockquote']; // Tags HTML permitidas

    // Expressão regular para encontrar tags HTML
    return text.replace(/<\/?([a-z][a-z0-9]*)\b[^>]*>/gi, (match, tag) => {
        if (allowedTags.includes(tag.toLowerCase())) {
            return match; // permite a tag
        } else {
            return match
                .replace(/&/g, "&amp;")
                .replace(/</g, "&lt;")
                .replace(/>/g, "&gt;");
        }
    }).replace(/"/g, "&quot;").replace(/'/g, "&#039;");
}

export function redirectToLink(link: string, navigate: any) {
  if (link.startsWith(origin)) {
    const relativePath = link.substring(origin.length) || "/";
    navigate(relativePath);
  } else {
    window.open(link, "_blank");
  }
}

export function formatBalance(value: number) {
  if (value >= 1_000_000) {
    return (value / 1_000_000).toFixed(2) + ' milhões';
  } else if (value >= 1_000) {
    return (value / 1_000).toFixed(2) + ' mil';
  } else {
    return value.toFixed(2);
  }
}

export const calculateTime = (created_at: any) => {
    const dataPostagem = new Date(created_at).getTime();
    const dataAtual = Date.now();
    const diferencaSegundos = Math.floor((dataAtual - dataPostagem) / 1000);

    if (diferencaSegundos < 60) {
        return t('time.justNow');
    }

    if (diferencaSegundos < 3600) {
        const minutos = Math.floor(diferencaSegundos / 60);
        return t('time.minute', { count: minutos });
    }

    if (diferencaSegundos < 86400) {
        const horas = Math.floor(diferencaSegundos / 3600);
        return t('time.hour', { count: horas });
    }

    if (diferencaSegundos < 2592000) {
        const dias = Math.floor(diferencaSegundos / 86400);
        return dias === 1
            ? t('time.yesterday')
            : t('time.day', { count: dias });
    }

    if (diferencaSegundos < 31536000) {
        const meses = Math.floor(diferencaSegundos / 2592000);
        return t('time.month', { count: meses });
    }

    const anos = Math.floor(diferencaSegundos / 31536000);
    return t('time.year', { count: anos });
};

export const calculateTimeReduce = (created_at: any) => {
    const dataPostagem = new Date(created_at).getTime();
    const dataAtual = Date.now();
    const diferencaSegundos = Math.floor((dataAtual - dataPostagem) / 1000);

    if (diferencaSegundos < 60) {
        return t('time.justNow');
    }

    if (diferencaSegundos < 3600) {
        const minutos = Math.floor(diferencaSegundos / 60);
        return `${minutos} min`;
    }

    if (diferencaSegundos < 86400) {
        const horas = Math.floor(diferencaSegundos / 3600);
        return `${horas} h`;
    }

    if (diferencaSegundos < 2592000) {
        const dias = Math.floor(diferencaSegundos / 86400);
        return `${dias} d`;
    }

    if (diferencaSegundos < 31536000) {
        const meses = Math.floor(diferencaSegundos / 2592000);
        return `${meses} m`;
    }

    const anos = Math.floor(diferencaSegundos / 31536000);
    return t('time.year', { count: anos });
};


const blacklist = [
    'estrupo',
    'estrupar',
    'estrupa',
    'caralho',
    // 'bunda',
    'hitler',
    // 'bunda',
    'desgraçado',
    // 'cadela',
    'chupador de pau',
    // 'besteira',
    'boceta',
    'cyka blyat',
    'droga',
    'pau',
    'sapatona',
    'porra',
    // 'inferno',
    'puta merda',
    // 'merda',
    // 'na merda',
    'kike',
    'filho da puta',
    'cu',
    'anus',
    'toba',
    'puta',
    'foda',
    'foda-se',
    'foder',
    'pariu',
    'nazista',
    'nazismo',
    'neonazismo',
    'neonazista',
    'odeio negro',
    'hitler',
    'anus',
    'arrombado',
    // 'babaca',
    // 'baita',
    // 'baleia',
    'baitola',
    'bicha',
    'bichona',
    'bichona velha',
    // 'biscate',
    'boceta',
    'boiola',
    // 'bosta',
    'bostinha',
    'boçal',
    'bundão',
    'bunduda',
    'cabeçudo',
    'cacete',
    'cadela',
    // 'cafajeste',
    'caga-regras',
    'cagão',
    'canalha',
    'capeta',
    'caralho',
    'capiroto',
    'chifrudo',
    'chupador de pau',
    'cocô',
    'corno',
    'cretino',
    'crackudo',
    'cretino',
    'cruel',
    'cu',
    'cuzão',
    'cyka blyat',
    'degenerado',
    'demente',
    'débil mental',
    'desgraça',
    'desgraçado',
    'diabo',
    // 'doente',
    // 'droga',
    'egocêntrico',
    'escroto',
    'esnobe',
    'estrupa',
    'estrupar',
    'estrupo',
    'estúpido',
    'fedorento',
    'filho da puta',
    'foda',
    'foda-se',
    'foder',
    'fudido',
    'gordo imundo',
    'imbecilóide',
    'kike',
    'lazarento',
    'lixo humano',
    'maldito',
    'mentecapto',
    'misógino',
    'na merda',
    'naba',
    'nazista',
    'nazismo',
    'neonazismo',
    'neonazista',
    // 'nojento',
    'nóia',
    'odeio negro',
    'otário',
    'paspalho',
    'pilantra',
    'piranha',
    'porra',
    'preto imundo',
    'prostituta',
    'puta',
    'puta merda',
    'quenga',
    // 'rato',
    'retardado',
    'sangrento',
    'sapatão',
    'sapatona',
    'satanás',
    'safado',
    'safadão',
    'seu lixo',
    'seu merda',
    'toba',
    'vadia',
    'vagabunda',
    'viado',
    'xvideos',
    'x videos',
    'xxx',
    'cuceta'
];

const blackListlink = [
    'xvideos.com',
    'pornhub.com',
    'onlyfans.com',
    'redtube.com',
    'xnxx.com',
    'youjizz.com',
    'xhamster.com',
    'youporn.com',
    'brazzerz.com', // erro comum de digitação de brazzers
    'brazzers.com',
    'spankbang.com',
    'fapello.com',
    'f95zone.to',
    'rule34.xxx',
    'hentaifox.com',
    'hentaihaven.xxx',
    'erome.com',
    'thothub.to',
    'tnaflix.com',
    'cam4.com',
    'livejasmin.com',
    'stripchat.com',
    'chaturbate.com',
    'nudevista.com',
    'javhd.com',
    'metart.com',
    'manyvids.com',
    'nsfw.xxx',
    'clips4sale.com',
    '53a7.xyz',
];

export const verifyLinks = (text: string): boolean =>{
    const lowerText = text.toLowerCase();

    return blackListlink.some(domain => lowerText.includes(domain.toLowerCase()));
}